import React, { useEffect, useRef, useState } from "react";
import { X, Wine, Globe, Tag, Trash2, Sparkles, MapPin, Calendar, Grape, Coins, Pencil, Save, FileDown, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import AppellationInsight from "@/components/AppellationInsight";
import { slugify } from "@/lib/slug";
import TastingPhotoControls from "@/components/TastingPhotoControls";
import { useT } from "@/lib/i18n";

/**
 * Full tasting-fiche modal with edit + PDF export + geoVINUM appellation map.
 * The `printable` div is what gets rasterised by html2pdf when the user
 * clicks "Exporter en PDF" (the map iframe is temporarily hidden during
 * export because cross-origin iframes cannot be captured by html2canvas).
 */
export default function TastingDetailModal({ tasting, onClose, onDelete, onUpdate }) {
  const { lang } = useT();
  const [editMode, setEditMode] = useState(false);
  const [form, setForm] = useState(() => ({ ...tasting, grapes: (tasting.grapes || []).join(", ") }));
  const [saving, setSaving] = useState(false);
  const [pdfBusy, setPdfBusy] = useState(false);
  const printRef = useRef(null);

  useEffect(() => {
    const onEsc = (e) => { if (e.key === "Escape" && !editMode) onClose(); };
    window.addEventListener("keydown", onEsc);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onEsc);
      document.body.style.overflow = "";
    };
  }, [onClose, editMode]);

  if (!tasting) return null;
  const t = editMode ? form : tasting;

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        product_type: form.product_type,
        name: form.name,
        year: form.year ? parseInt(form.year, 10) : null,
        producer: form.producer || null,
        region: form.region || null,
        appellation: form.appellation || null,
        grapes: form.grapes ? String(form.grapes).split(",").map((s) => s.trim()).filter(Boolean) : [],
        robe: form.robe || null,
        nez: form.nez || null,
        bouche: form.bouche || null,
        score: form.score !== "" && form.score != null ? parseFloat(form.score) : null,
        comment: form.comment || null,
        photo_url: form.photo_url || null,
        web_photo_url: form.web_photo_url || null,
        user_photo_url: form.user_photo_url || null,
      };
      const { data } = await api.patch(`/tastings/${tasting.id}`, payload);
      onUpdate && onUpdate(data);
      setEditMode(false);
      toast.success("Fiche mise à jour");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Sauvegarde impossible");
    } finally { setSaving(false); }
  };

  const exportPdf = async () => {
    if (!printRef.current) return;
    setPdfBusy(true);
    try {
      // Lazy import to keep initial bundle small
      const html2pdf = (await import("html2pdf.js")).default;
      const node = printRef.current;
      node.classList.add("printing"); // hides interactive-only bits (see .printing CSS below)
      await html2pdf()
        .from(node)
        .set({
          margin: [10, 10],
          filename: `geovinum-${(tasting.name || "degustation").toLowerCase().replace(/[^a-z0-9]+/g, "-")}.pdf`,
          image: { type: "jpeg", quality: 0.95 },
          html2canvas: { scale: 2, useCORS: true, allowTaint: false, backgroundColor: "#ffffff" },
          jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
        })
        .save();
      toast.success("PDF téléchargé");
    } catch (e) {
      toast.error("Export PDF impossible");
    } finally {
      printRef.current?.classList.remove("printing");
      setPdfBusy(false);
    }
  };

  const appellationSlug = t.appellation ? slugify(t.appellation) : null;

  return (
    <div
      data-testid="tasting-detail-modal"
      className="fixed inset-0 z-[80] flex items-end md:items-center justify-center bg-black/50 backdrop-blur-sm md:p-4 overlay-safe"
      onClick={(e) => { if (e.target === e.currentTarget && !editMode) onClose(); }}
    >
      <div className="relative w-full max-w-4xl bg-white shadow-2xl overflow-hidden flex flex-col
                      h-[calc(100dvh_-_var(--safe-top)_-_0.5rem)] md:h-auto md:max-h-[90dvh] pb-safe md:pb-0
                      rounded-t-3xl md:rounded-3xl">
        {/* Sticky header — always visible so the close button never scrolls out */}
        <div className="sticky top-0 z-20 flex items-center justify-between gap-3 px-4 py-3
                        bg-white/95 backdrop-blur border-b border-[#F0EAE0] shrink-0">
          <div className="flex items-center gap-2 min-w-0">
            <Wine className="w-4 h-4 text-[#E5276A] shrink-0" />
            <span className="font-bold text-[#1F3A5F] text-sm truncate">
              {t.name || (editMode ? "Nouvelle fiche" : "Fiche de dégustation")}
            </span>
          </div>
          <button
            onClick={onClose}
            aria-label="Fermer"
            data-testid="tasting-detail-close"
            className="w-10 h-10 rounded-full bg-[#FBF7F3] hover:bg-[#FCE4ED] flex items-center justify-center text-[#1F3A5F] hover:text-[#E5276A] transition-colors shrink-0"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="grid md:grid-cols-[300px,1fr] flex-1 min-h-0 overflow-y-auto" ref={printRef}>
          {/* Photo panel — full-width on mobile with capped height, sidebar on desktop */}
          <div className="bg-gradient-to-br from-[#FBF7F3] to-[#FCE4ED]/40 p-4 md:p-6 flex flex-col gap-3 items-center justify-center md:min-h-[520px]">
            {t.photo_url ? (
              <img src={t.photo_url} alt={t.name} data-testid="tasting-detail-photo"
                className="w-full max-w-[220px] max-h-[180px] md:max-h-[420px] object-contain rounded-xl shadow-lg"
                onError={(e) => { e.currentTarget.style.display = "none"; }} />
            ) : (
              <div className="w-32 h-40 md:w-40 md:h-52 rounded-xl bg-white flex items-center justify-center">
                <Wine className="w-14 h-14 md:w-16 md:h-16 text-[#E5276A]/40" />
              </div>
            )}
            {editMode && <TastingPhotoControls form={form} setForm={setForm} lang={lang} testIdPrefix="tasting-edit-photo" />}
          </div>

          {/* Content panel — the whole grid scrolls, so no inner overflow needed */}
          <div className="p-5 md:p-8">
            {editMode ? (
              /* ────── Edit form ────── */
              <div className="space-y-3" data-testid="tasting-edit-form">
                <div className="text-[10px] uppercase tracking-widest text-[#E5276A] font-bold inline-flex items-center gap-1.5">
                  <Pencil className="w-3 h-3" /> Édition
                </div>
                <Row><EditField label="Type" value={t.product_type || ""} onChange={(v) => setForm({ ...form, product_type: v })} /></Row>
                <Row><EditField label="Nom" value={t.name || ""} onChange={(v) => setForm({ ...form, name: v })} /></Row>
                <Row cols={2}>
                  <EditField label="Producteur" value={t.producer || ""} onChange={(v) => setForm({ ...form, producer: v })} />
                  <EditField label="Année" type="number" value={t.year || ""} onChange={(v) => setForm({ ...form, year: v })} />
                </Row>
                <Row cols={2}>
                  <EditField label="Région" value={t.region || ""} onChange={(v) => setForm({ ...form, region: v })} />
                  <EditField label="Appellation" value={t.appellation || ""} onChange={(v) => setForm({ ...form, appellation: v })} />
                </Row>
                <Row><EditField label="Cépages (virgules)" value={t.grapes || ""} onChange={(v) => setForm({ ...form, grapes: v })} /></Row>
                <Row><EditField label="Robe" textarea rows={2} value={t.robe || ""} onChange={(v) => setForm({ ...form, robe: v })} /></Row>
                <Row><EditField label="Nez" textarea rows={2} value={t.nez || ""} onChange={(v) => setForm({ ...form, nez: v })} /></Row>
                <Row><EditField label="Bouche" textarea rows={2} value={t.bouche || ""} onChange={(v) => setForm({ ...form, bouche: v })} /></Row>
                <Row><EditField label="Note /20" type="number" step="0.5" value={t.score ?? ""} onChange={(v) => setForm({ ...form, score: v })} /></Row>
                <Row><EditField label="Commentaire" textarea rows={4} value={t.comment || ""} onChange={(v) => setForm({ ...form, comment: v })} /></Row>
                <div className="pt-3 flex items-center gap-2">
                  <button onClick={save} disabled={saving} data-testid="tasting-edit-save"
                    className="inline-flex items-center gap-1.5 px-5 py-2 rounded-full bg-[#E5276A] text-white text-sm font-bold hover:bg-[#C01661] disabled:opacity-60">
                    {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Enregistrer
                  </button>
                  <button onClick={() => { setEditMode(false); setForm({ ...tasting, grapes: (tasting.grapes || []).join(", ") }); }}
                    className="px-5 py-2 rounded-full text-sm font-bold text-[#8B95A1] hover:text-[#E5276A]">
                    Annuler
                  </button>
                </div>
              </div>
            ) : (
              /* ────── Read-only view ────── */
              <>
                <div className="text-[10px] uppercase tracking-widest text-[#E5276A] font-bold inline-flex items-center gap-1.5">
                  <Tag className="w-3 h-3" /> {t.product_type}
                  {t.year ? <><span className="text-[#8B95A1]">·</span><Calendar className="w-3 h-3" /> {t.year}</> : null}
                </div>
                <h2 className="font-display text-3xl md:text-4xl text-[#1F3A5F] mt-2 leading-tight" data-testid="tasting-detail-name">{t.name}</h2>
                {t.producer && (
                  <div className="text-sm text-[#4A5568] mt-1">
                    <span className="text-[#8B95A1]">Producteur :</span> <b>{t.producer}</b>
                  </div>
                )}

                {(t.region || t.appellation || (t.grapes && t.grapes.length > 0)) && (
                  <div className="mt-4 flex flex-wrap gap-2">
                    {t.region && (
                      <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-[#FBF7F3] text-xs font-bold text-[#1F3A5F]">
                        <MapPin className="w-3 h-3 text-[#E5276A]" /> {t.region}
                      </span>
                    )}
                    {t.appellation && (
                      <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-[#FCE4ED] text-xs font-bold text-[#C01661]">
                        {t.appellation}
                      </span>
                    )}
                    {(t.grapes || []).map((g) => (
                      <span key={g} className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-white border border-[#1F3A5F]/12 text-xs font-bold text-[#1F3A5F]">
                        <Grape className="w-3 h-3 text-[#E5276A]" /> {g}
                      </span>
                    ))}
                  </div>
                )}

                {t.score != null && (
                  <div className="mt-5 inline-flex items-baseline gap-1 px-5 py-3 rounded-2xl bg-[#E5276A] text-white">
                    <span className="font-display text-4xl leading-none" data-testid="tasting-detail-score">{t.score}</span>
                    <span className="text-sm opacity-80">/20</span>
                  </div>
                )}

                {(t.robe || t.nez || t.bouche) && (
                  <div className="mt-6 grid sm:grid-cols-3 gap-3">
                    {t.robe && <NoteBlock label="Robe" value={t.robe} testid="tasting-detail-robe" />}
                    {t.nez && <NoteBlock label="Nez" value={t.nez} testid="tasting-detail-nez" />}
                    {t.bouche && <NoteBlock label="Bouche" value={t.bouche} testid="tasting-detail-bouche" />}
                  </div>
                )}

                {t.comment && t.comment !== t.raw_notes && (
                  <div className="mt-6 p-4 rounded-2xl bg-gradient-to-br from-[#FBF7F3] to-white border border-[#E5276A]/15" data-testid="tasting-detail-comment">
                    <div className="text-[10px] uppercase tracking-widest text-[#E5276A] font-bold inline-flex items-center gap-1.5 mb-1.5">
                      <Sparkles className="w-3 h-3" /> Synthèse geoVINUM AI
                    </div>
                    <p className="text-sm text-[#1F3A5F] leading-relaxed whitespace-pre-line">{t.comment}</p>
                  </div>
                )}

                {/* geoVINUM appellation map */}
                {t.appellation && (
                  <div className="mt-6 hide-in-pdf" data-testid="tasting-detail-map">
                    <div className="text-[10px] uppercase tracking-widest text-[#E5276A] font-bold mb-2">
                      Carte geoVINUM
                    </div>
                    <AppellationInsight
                      appellation={{ name: t.appellation, region: t.region, country: null, slug: appellationSlug }}
                      defaultOpen={false}
                    />
                  </div>
                )}

                {t.raw_notes && (
                  <details className="mt-4 rounded-xl border border-[#1F3A5F]/12 p-3 hide-in-pdf" data-testid="tasting-detail-raw">
                    <summary className="cursor-pointer text-[10px] uppercase tracking-widest text-[#8B95A1] font-bold hover:text-[#E5276A]">
                      Voir mes notes originales
                    </summary>
                    <p className="mt-2 text-sm text-[#4A5568] whitespace-pre-line italic">« {t.raw_notes} »</p>
                  </details>
                )}

                {(t.domain_url || t.price_indication) && (
                  <div className="mt-6 flex flex-wrap items-center gap-4 text-sm">
                    {t.domain_url && (
                      <a href={t.domain_url} target="_blank" rel="noreferrer" data-testid="tasting-detail-domain"
                        className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-[#1F3A5F] text-white font-bold hover:bg-[#2A4870]">
                        <Globe className="w-4 h-4" /> Site du domaine
                      </a>
                    )}
                    {t.price_indication && (
                      <div className="inline-flex items-center gap-1.5 text-[#4A5568]">
                        <Coins className="w-4 h-4 text-[#E5276A]" />
                        <span className="text-xs uppercase tracking-wider text-[#8B95A1] font-bold">Prix indicatif :</span>
                        <b className="text-[#1F3A5F]">{t.price_indication}</b>
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {/* Action bar (hidden in edit mode) */}
        {!editMode && (
          <div className="border-t border-[#1F3A5F]/8 px-6 md:px-8 py-4 flex flex-wrap items-center justify-between gap-2 bg-white hide-in-pdf">
            <div className="flex items-center gap-2">
              <button onClick={() => setEditMode(true)} data-testid="tasting-edit-btn"
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-[#1F3A5F] text-white text-sm font-bold hover:bg-[#2A4870]">
                <Pencil className="w-4 h-4" /> Éditer la fiche
              </button>
              <button onClick={exportPdf} disabled={pdfBusy} data-testid="tasting-pdf-btn"
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full border-2 border-[#1F3A5F]/15 text-sm font-bold text-[#1F3A5F] hover:border-[#E5276A] disabled:opacity-50">
                {pdfBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileDown className="w-4 h-4" />} Exporter en PDF
              </button>
            </div>
            <button onClick={() => { if (window.confirm("Supprimer cette dégustation ?")) { onDelete(tasting.id); onClose(); } }} data-testid="tasting-detail-delete"
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-[#E5276A] hover:bg-[#FCE4ED] text-sm font-bold">
              <Trash2 className="w-4 h-4" /> Supprimer
            </button>
          </div>
        )}
      </div>

      {/* PDF export: hide iframe + interactive-only blocks during rasterization */}
      <style>{`
        .printing .hide-in-pdf { display: none !important; }
      `}</style>
    </div>
  );
}

function NoteBlock({ label, value, testid }) {
  return (
    <div className="p-4 rounded-xl bg-white border border-[#1F3A5F]/10" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-widest text-[#E5276A] font-bold mb-1.5">{label}</div>
      <p className="text-sm text-[#1F3A5F] leading-relaxed">{value}</p>
    </div>
  );
}

function Row({ children, cols = 1 }) {
  const map = { 1: "", 2: "grid grid-cols-2 gap-3", 3: "grid grid-cols-3 gap-3" };
  return <div className={map[cols]}>{children}</div>;
}

function EditField({ label, value, onChange, type = "text", textarea = false, rows = 2, step }) {
  const cls = "w-full px-3 py-2 rounded-lg border border-[#1F3A5F]/15 focus:border-[#E5276A] focus:ring-2 focus:ring-[#E5276A]/10 outline-none text-sm text-[#1F3A5F] bg-white";
  return (
    <label className="block">
      <span className="text-[10px] uppercase tracking-widest text-[#8B95A1] font-bold mb-1 block">{label}</span>
      {textarea ? (
        <textarea rows={rows} className={cls} value={value} onChange={(e) => onChange(e.target.value)} />
      ) : (
        <input type={type} step={step} className={cls} value={value} onChange={(e) => onChange(e.target.value)} />
      )}
    </label>
  );
}
